import { Image as ImageIcon, Loader2, ImageOff, Table as TableIcon, Maximize2 } from "lucide-react";
import { useAuthedImage } from "../../hooks/useAuthedImage";
import { sanitizeTableSnippet } from "./ChatSourceCard";

export interface InlineVisualFigure {
  kind: "figure";
  n: number;
  document_id: string;
  fig_id: string;
  page: number;
  caption: string | null;
  // External-Source-Pfad (cosmetics_facts via doc-ai brain_store):
  // wenn beide gesetzt sind → URL geht über /projects/external-sources/
  source_hash?: string | null;
  external_source_slug?: string | null;
}

export interface InlineVisualTable {
  kind: "table";
  n: number;
  document_id: string;
  table_id: string;
  page: number;
  caption: string | null;
  summary: string | null;
  html_preview: string | null;
  source_hash?: string | null;
  external_source_slug?: string | null;
}

export type InlineVisualSource = InlineVisualFigure | InlineVisualTable;

interface Props {
  source: InlineVisualSource;
  onClick?: () => void;
}

export function InlineVisual({ source, onClick }: Props) {
  if (source.kind === "figure") return <FigureInline source={source} onClick={onClick} />;
  return <TableInline source={source} onClick={onClick} />;
}

function FigureInline({ source, onClick }: { source: InlineVisualFigure; onClick?: () => void }) {
  // External-Source (cosmetics_facts via brain_store) hat keine local doc — Bytes
  // kommen über /projects/external-sources/{slug}/figures/{source_hash}/{fig_id}.
  // Sonst klassisch über /documents/{id}/figures/{fig_id}.
  const path =
    source.external_source_slug && source.source_hash
      ? `/projects/external-sources/${source.external_source_slug}/figures/${source.source_hash}/${encodeURIComponent(source.fig_id)}`
      : `/documents/${source.document_id}/figures/${encodeURIComponent(source.fig_id)}`;
  const { src, loading, error } = useAuthedImage(path);
  return (
    <figure className="not-prose my-3 mx-auto max-w-xl">
      <button
        type="button"
        onClick={onClick}
        className="group relative block w-full bg-cream-50 dark:bg-ink-800/60 rounded-lg border border-cream-200 dark:border-ink-700 overflow-hidden hover:border-brand-400 dark:hover:border-brand-500 transition-colors"
      >
        <div className="min-h-[140px] flex items-center justify-center p-2">
          {loading && <Loader2 className="w-5 h-5 text-ink-50/40 animate-spin" />}
          {error && (
            <div className="text-[11px] text-ink-50/50 inline-flex items-center gap-1.5">
              <ImageOff className="w-4 h-4" /> Bild nicht verfügbar
            </div>
          )}
          {src && (
            <img
              src={src}
              alt={source.caption ?? `Bild zur Quelle ${source.n}`}
              loading="lazy"
              className="max-w-full max-h-[360px] w-auto h-auto object-contain"
            />
          )}
        </div>
        {src && (
          <span className="absolute top-2 right-2 bg-black/55 text-white text-[10px] px-1.5 py-0.5 rounded-md inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize2 className="w-3 h-3" /> Quelle {source.n}
          </span>
        )}
      </button>
      <figcaption className="mt-1.5 flex items-start gap-1.5 text-[11px] text-ink-50/70">
        <ImageIcon className="w-3 h-3 mt-0.5 shrink-0 text-brand-500" />
        <span className="leading-snug">
          <span className="font-medium text-brand-600 dark:text-brand-400">
            Quelle {source.n}
          </span>{" "}
          · Bild · Seite {source.page}
          {source.caption && (
            <span className="block text-ink-50/60 mt-0.5">
              {source.caption}
            </span>
          )}
        </span>
      </figcaption>
    </figure>
  );
}

function TableInline({ source, onClick }: { source: InlineVisualTable; onClick?: () => void }) {
  const cleanHtml = source.html_preview ? sanitizeTableSnippet(source.html_preview) : null;
  return (
    <figure className="not-prose my-3 mx-auto max-w-xl">
      <button
        type="button"
        onClick={onClick}
        className="group block w-full text-left bg-cream-50 dark:bg-ink-800/60 rounded-lg border border-cream-200 dark:border-ink-700 px-3 py-2.5 hover:border-brand-400 dark:hover:border-brand-500 transition-colors"
      >
        <div className="flex items-start gap-2">
          <TableIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand-500" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="text-[11px] text-ink-50/70">
              <span className="font-medium text-brand-600 dark:text-brand-400">
                Quelle {source.n}
              </span>{" "}
              · Tabelle · Seite {source.page}
            </div>
            {source.caption && (
              <p className="text-[12.5px] italic text-ink-50/80 leading-snug">
                {source.caption}
              </p>
            )}
            {source.summary && (
              <p className="text-[12px] text-ink-50/70 leading-relaxed">
                {source.summary}
              </p>
            )}
            {!source.summary && cleanHtml && (
              <div
                className="overflow-x-auto rounded-md border border-cream-200 dark:border-ink-600 bg-surface/60 dark:bg-ink-900/40 [&_table]:w-full [&_table]:text-[11px] [&_table]:border-collapse [&_th]:bg-cream-100 [&_th]:dark:bg-ink-800 [&_th]:font-medium [&_th]:text-left [&_th]:p-1.5 [&_th]:border [&_th]:border-cream-200 [&_th]:dark:border-ink-700 [&_td]:p-1.5 [&_td]:border [&_td]:border-cream-200 [&_td]:dark:border-ink-700 [&_td]:align-top"
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
              />
            )}
            {!source.summary && !cleanHtml && (
              <p className="text-[11px] text-ink-50/50 italic">
                Tabelle in der Quellen-Liste öffnen für die Vollansicht.
              </p>
            )}
          </div>
        </div>
      </button>
    </figure>
  );
}
